'use client';

import { useCallback, useState } from 'react';

import { FileRejection, useDropzone } from 'react-dropzone';
import clsx from 'clsx';

import { toast_error, toast_warning } from '@/libs/toast-wrappers';
import { putCaseFile } from '@/api/cases';

type ImageUploaderProps = {
  caseId: string;
  images?: File[];
  onUploadSuccess: (file: File) => void;
  disabled?: boolean;
};

export function ImageUploader({ images, caseId, onUploadSuccess, disabled }: ImageUploaderProps) {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (!caseId) return;

      if (fileRejections.length > 0) {
        toast_warning('File type not supported');
      }
      if (acceptedFiles.length === 0) return;

      // we need to check if the file is already in the list
      if (acceptedFiles.some((file) => images?.some((img) => img.name === file.name))) {
        toast_warning('A file with the same name already exists');
        return;
      }

      // upload all the accepted files
      setIsUploading(true);
      setUploadProgress(0);
      for (const file of acceptedFiles) {
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
          await putCaseFile(caseId, file.name, file);
          onUploadSuccess(file);
          setUploadProgress(100);
        } catch {
          toast_error('Error uploading file: ' + file.name);
        }
      }
      setUploadProgress(100);
      setIsUploading(false);
    },
    [caseId, images, onUploadSuccess]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    accept: { 'image/*': [] },
  });

  if (isUploading && uploadProgress < 100) {
    setTimeout(() => setUploadProgress((prev) => Math.min(prev + 10, 100)), 1000);
  }

  return (
    <div className="w-full my-8">
      <div
        {...getRootProps()}
        className={clsx(
          'border-2 border-dashed rounded p-6 text-center cursor-pointer hover:bg-gray-50 transition',
          { 'bg-gray-100': isDragActive, 'opacity-50': disabled }
        )}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the image here...</p>
        ) : (
          <p>Drag and drop an image, or click to select</p>
        )}
      </div>

      {isUploading && (
        <div className="mt-4 w-full bg-gray-200 h-1 overflow-hidden">
          <div
            className="bg-secondary h-full transition-all duration-200"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
    </div>
  );
}
